import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import SEO from './SEO';
import { trackPhoneClick } from './analytics';

/**
 * Individual Service Detail Page
 * 
 * Each service gets its own URL for better SEO targeting:
 * - /services/commercial-construction
 * - /services/office-remodeling
 * - /services/industrial-construction
 * etc.
 */

const serviceData = {
  'commercial-construction': {
    title: 'Commercial Building Construction',
    metaTitle: 'Commercial Construction Chicago | Commercial Building Contractor',
    metaDescription: 'Expert commercial building construction in Chicago. Office buildings, retail centers, mixed-use developments. Licensed general contractor with 20+ years experience. Call (312) 675-6000.',
    keywords: 'commercial construction chicago, commercial building contractor chicago, commercial construction companies chicago, office building construction chicago',
    heroImage: '/office2.png',
    description: `Freedom Contracting Group is Chicago's premier commercial construction company, delivering 
    high-quality building projects for businesses throughout Illinois. Our experienced team handles all aspects 
    of commercial construction, from initial design through final completion.`,
    longDescription: `With over 20 years of experience in commercial construction, Freedom Contracting Group 
    has established itself as one of Chicago's most trusted general contractors. We specialize in ground-up 
    commercial construction for a wide range of building types, including office buildings, retail centers, 
    mixed-use developments, and corporate facilities.
    
    Our comprehensive approach to commercial construction ensures that every project is completed on time, 
    within budget, and to the highest quality standards. We work closely with architects, engineers, and 
    building owners to deliver spaces that meet both functional requirements and aesthetic goals.`,
    services: [
      {
        name: 'Ground-Up Construction',
        description: 'Complete new construction services from site preparation to certificate of occupancy.'
      },
      {
        name: 'Design-Build Services',
        description: 'Streamlined delivery method combining design and construction under one contract.'
      },
      {
        name: 'Pre-Construction Planning',
        description: 'Budgeting, scheduling, and value engineering to optimize your project before construction begins.'
      },
      {
        name: 'Core & Shell Construction',
        description: 'Building structure and exterior construction for multi-tenant developments.'
      },
      {
        name: 'Building Permits & Compliance',
        description: 'Navigate Chicago building codes and permit requirements efficiently.'
      }
    ],
    projectTypes: [
      'Office Buildings',
      'Retail Centers',
      'Mixed-Use Developments',
      'Corporate Headquarters',
      'Professional Buildings',
      'Multi-Tenant Properties'
    ],
    stats: [
      { value: '500+', label: 'Commercial Projects Completed' },
      { value: '2M+', label: 'Square Feet Built' },
      { value: '20+', label: 'Years Experience' }
    ]
  },
  'office-remodeling': {
    title: 'Commercial Office Remodeling',
    metaTitle: 'Commercial Office Remodeling Chicago | Office Renovation Contractor',
    metaDescription: 'Professional commercial office remodeling in Chicago. Tenant improvements, office build-outs, corporate renovations. Transform your workspace with Freedom Contracting Group. Call (312) 675-6000.',
    keywords: 'commercial office remodeling chicago, office renovation chicago, tenant improvement contractor chicago, office build-out chicago, commercial renovation chicago',
    heroImage: '/office1.jpeg',
    description: `Transform your commercial workspace with Freedom Contracting Group's expert office 
    remodeling services. We specialize in tenant improvements, office build-outs, and complete 
    corporate renovations throughout the Chicago area.`,
    longDescription: `Whether you're updating an existing office space or building out a new location, 
    Freedom Contracting Group delivers exceptional commercial office remodeling services that minimize 
    disruption to your business operations. Our team understands that every workspace is unique, and 
    we work closely with business owners to create environments that enhance productivity, reflect 
    brand identity, and meet the evolving needs of modern workplaces.
    
    From conference room upgrades to complete floor renovations, we handle projects of all sizes with 
    the same commitment to quality and customer service that has made us one of Chicago's most trusted 
    commercial contractors.`,
    services: [
      {
        name: 'Tenant Improvements',
        description: 'Customize leased spaces to meet your specific business requirements and brand standards.'
      },
      {
        name: 'Office Build-Outs',
        description: 'Complete construction of interior spaces from raw shell to move-in ready offices.'
      },
      {
        name: 'Space Planning & Design',
        description: 'Optimize your office layout for efficiency, collaboration, and employee comfort.'
      },
      {
        name: 'ADA Compliance Upgrades',
        description: 'Ensure your workspace meets all accessibility requirements and regulations.'
      },
      {
        name: 'Sustainable Renovations',
        description: 'Green building practices and energy-efficient upgrades for environmentally conscious businesses.'
      }
    ],
    projectTypes: [
      'Corporate Offices',
      'Law Firms',
      'Medical Offices',
      'Financial Institutions',
      'Tech Companies',
      'Professional Services'
    ],
    stats: [
      { value: '300+', label: 'Office Projects Completed' },
      { value: '1M+', label: 'Square Feet Renovated' },
      { value: '98%', label: 'On-Time Completion Rate' }
    ]
  },
  'industrial-construction': {
    title: 'Industrial & Warehouse Construction',
    metaTitle: 'Industrial Construction Chicago | Warehouse Building Contractor',
    metaDescription: 'Industrial and warehouse construction services in Chicago. Manufacturing facilities, distribution centers, logistics buildings. Licensed industrial contractor. Call (312) 675-6000.',
    keywords: 'industrial construction chicago, warehouse construction chicago, manufacturing facility construction, distribution center construction chicago',
    heroImage: '/fcbghero1.webp',
    description: `Freedom Contracting Group provides expert industrial and warehouse construction 
    services for manufacturing, distribution, and logistics operations throughout the Chicago area.`,
    longDescription: `As Chicago's logistics and manufacturing hub continues to grow, Freedom Contracting 
    Group delivers the industrial construction services businesses need to compete. Our experienced team 
    specializes in building functional, efficient facilities that optimize workflow, reduce operational 
    costs, and position your business for long-term success.
    
    From small warehouse additions to large-scale distribution centers, we bring the same attention to 
    detail and commitment to quality that has earned us a reputation as one of the region's most reliable 
    industrial construction contractors.`,
    services: [
      {
        name: 'Warehouse Construction',
        description: 'High-ceiling storage facilities with efficient layouts and modern systems.'
      },
      {
        name: 'Manufacturing Facilities',
        description: 'Production spaces designed for your specific manufacturing processes and equipment.'
      },
      {
        name: 'Distribution Centers',
        description: 'Logistics-optimized buildings with advanced loading and storage systems.'
      },
      {
        name: 'Cold Storage Facilities',
        description: 'Temperature-controlled environments for food, pharmaceutical, and specialty storage.'
      },
      {
        name: 'Loading Dock Installation',
        description: 'Efficient dock configurations for streamlined logistics operations.'
      }
    ],
    projectTypes: [
      'Warehouses',
      'Distribution Centers',
      'Manufacturing Plants',
      'Food Processing Facilities',
      'Logistics Centers',
      'Industrial Parks'
    ],
    stats: [
      { value: '200+', label: 'Industrial Projects' },
      { value: '5M+', label: 'Square Feet Built' },
      { value: '50+', label: 'Manufacturing Clients' }
    ]
  },
  'healthcare-construction': {
    title: 'Healthcare Facility Construction',
    metaTitle: 'Healthcare Construction Chicago | Medical Office Building Contractor',
    metaDescription: 'Specialized healthcare construction in Chicago. Hospitals, medical offices, clinics, dental practices. HIPAA-compliant facilities. Call Freedom Contracting Group at (312) 675-6000.',
    keywords: 'healthcare construction chicago, medical office construction chicago, hospital construction chicago, dental office construction, clinic construction',
    heroImage: '/fcbghero1.webp',
    description: `Freedom Contracting Group specializes in healthcare facility construction, delivering 
    compliant, patient-centered spaces for hospitals, medical offices, clinics, and specialty practices.`,
    longDescription: `Healthcare construction requires specialized expertise to meet strict regulatory 
    requirements while creating environments that promote patient comfort and operational efficiency. 
    Freedom Contracting Group brings decades of experience building healthcare facilities throughout 
    the Chicago area, from small dental practices to large ambulatory care centers.
    
    Our team understands the unique challenges of healthcare construction, including infection control, 
    medical gas systems, specialized HVAC requirements, and the need to minimize disruption to ongoing 
    patient care during renovations.`,
    services: [
      {
        name: 'Hospital Construction',
        description: 'New construction and renovations for hospital facilities and departments.'
      },
      {
        name: 'Medical Office Build-Outs',
        description: 'Custom physician offices and specialty practice spaces.'
      },
      {
        name: 'Ambulatory Care Centers',
        description: 'Outpatient surgery centers and same-day procedure facilities.'
      },
      {
        name: 'Dental Office Construction',
        description: 'Modern dental practices with specialized equipment infrastructure.'
      },
      {
        name: 'HIPAA-Compliant Facilities',
        description: 'Privacy-focused design meeting all healthcare data protection requirements.'
      }
    ],
    projectTypes: [
      'Hospitals',
      'Medical Office Buildings',
      'Urgent Care Centers',
      'Dental Practices',
      'Physical Therapy Clinics',
      'Imaging Centers'
    ],
    stats: [
      { value: '100+', label: 'Healthcare Projects' },
      { value: '500K+', label: 'Square Feet Built' },
      { value: '100%', label: 'Compliance Rate' }
    ]
  },
  'retail-construction': {
    title: 'Retail & Restaurant Construction',
    metaTitle: 'Retail Construction Chicago | Restaurant Build-Out Contractor',
    metaDescription: 'Retail store and restaurant construction in Chicago. Store build-outs, restaurant construction, shopping center renovations. Call Freedom Contracting Group at (312) 675-6000.',
    keywords: 'retail construction chicago, restaurant construction chicago, store build-out chicago, retail contractor chicago, restaurant build-out',
    heroImage: '/fcbghero1.webp',
    description: `Build your retail or restaurant presence with Freedom Contracting Group's expert 
    construction services. From boutiques to big-box retailers, we deliver projects that attract 
    customers and drive business success.`,
    longDescription: `In the competitive retail and restaurant industry, your physical space is a crucial 
    part of your brand. Freedom Contracting Group helps businesses create environments that attract 
    customers, enhance the shopping or dining experience, and support efficient operations. Our team 
    has extensive experience with retail build-outs, restaurant construction, and shopping center 
    renovations throughout the Chicago area.
    
    We understand the importance of speed to market in retail and work efficiently to minimize 
    construction time while maintaining the highest quality standards.`,
    services: [
      {
        name: 'Retail Store Build-Outs',
        description: 'Custom retail spaces from design through grand opening.'
      },
      {
        name: 'Restaurant Construction',
        description: 'Full-service and quick-service restaurant construction with kitchen expertise.'
      },
      {
        name: 'Shopping Center Renovations',
        description: 'Multi-tenant retail center improvements and common area upgrades.'
      },
      {
        name: 'Storefront Installation',
        description: 'Eye-catching entrances and window displays that draw customers.'
      },
      {
        name: 'Quick-Service Restaurant Builds',
        description: 'Fast-casual and QSR construction with efficiency-focused design.'
      }
    ],
    projectTypes: [
      'Retail Stores',
      'Restaurants',
      'Shopping Centers',
      'Fast Food Locations',
      'Boutiques',
      'Grocery Stores'
    ],
    stats: [
      { value: '150+', label: 'Retail Projects' },
      { value: '75+', label: 'Restaurant Builds' },
      { value: '4-8 Weeks', label: 'Average Build Time' }
    ]
  },
  'project-management': {
    title: 'Construction Management Services',
    metaTitle: 'Construction Management Chicago | General Contracting Services',
    metaDescription: 'Professional construction management and general contracting services in Chicago. Project scheduling, budget management, quality control. Call Freedom Contracting Group at (312) 675-6000.',
    keywords: 'construction management chicago, general contracting services chicago, construction project management, construction manager chicago',
    heroImage: '/fcbghero1.webp',
    description: `Freedom Contracting Group provides comprehensive construction management and general 
    contracting services, ensuring your project is completed on time, within budget, and to the 
    highest quality standards.`,
    longDescription: `Effective construction management is the foundation of successful project delivery. 
    Freedom Contracting Group's experienced project managers bring decades of combined experience to 
    every project, coordinating all aspects of construction from pre-construction planning through 
    final completion.
    
    Our construction management services provide building owners with a single point of accountability, 
    transparent communication, and the peace of mind that comes from working with a trusted partner 
    who has successfully delivered hundreds of projects throughout the Chicago area.`,
    services: [
      {
        name: 'Project Scheduling',
        description: 'Detailed construction schedules with milestone tracking and progress reporting.'
      },
      {
        name: 'Budget Management',
        description: 'Cost control systems and regular financial updates throughout construction.'
      },
      {
        name: 'Subcontractor Coordination',
        description: 'Expert management of trade contractors for seamless project execution.'
      },
      {
        name: 'Quality Control',
        description: 'Rigorous inspection protocols ensuring construction meets specifications.'
      },
      {
        name: 'Progress Reporting',
        description: 'Regular updates and documentation keeping stakeholders informed.'
      }
    ],
    projectTypes: [
      'All Commercial Projects',
      'Multi-Phase Developments',
      'Renovation Projects',
      'New Construction',
      'Fast-Track Projects',
      'Design-Build Projects'
    ],
    stats: [
      { value: '1,000+', label: 'Projects Managed' },
      { value: '95%', label: 'On-Budget Completion' },
      { value: '97%', label: 'On-Time Delivery' }
    ]
  }
};

function ServiceDetail() {
  const { serviceId } = useParams();
  const service = serviceData[serviceId];

  // If service doesn't exist, redirect to services page
  if (!service) {
    return <Navigate to="/services" replace />;
  }

  // Generate FAQ schema
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: `What is included in your ${service.title.toLowerCase()} services?`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: service.services.map(s => s.name).join(', ') + '. Contact us for a detailed scope of work.'
        }
      },
      {
        '@type': 'Question',
        name: `How much does ${service.title.toLowerCase()} cost in Chicago?`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Costs vary based on project scope, size, and specifications. Contact Freedom Contracting Group at (312) 675-6000 for a free estimate tailored to your specific project requirements.'
        }
      },
      {
        '@type': 'Question',
        name: `How long does a typical ${service.title.toLowerCase()} project take?`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Project timelines depend on scope and complexity. Small projects may take 4-8 weeks, while larger projects can take several months. We provide detailed schedules during the proposal phase.'
        }
      }
    ]
  };

  // Service schema
  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: service.title,
    provider: {
      '@type': 'GeneralContractor',
      name: 'Freedom Contracting Group',
      telephone: '+1-312-675-6000',
      url: 'https://freedomcg.net'
    },
    areaServed: {
      '@type': 'City',
      name: 'Chicago',
      containedInPlace: {
        '@type': 'State',
        name: 'Illinois'
      }
    },
    description: service.description
  };

  return (
    <div className="min-h-screen bg-slate-100">
      <SEO 
        title={service.metaTitle}
        description={service.metaDescription}
        keywords={service.keywords}
        path={`/services/${serviceId}`}
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'Services', url: '/services' },
          { name: service.title }
        ]}
        additionalSchema={[faqSchema, serviceSchema]}
      />

      {/* Header */}
      <div className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-200 shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div>
              <nav className="text-sm text-gray-500 mb-1">
                <Link to="/" className="hover:text-blue-900">Home</Link>
                <span className="mx-2">/</span>
                <Link to="/services" className="hover:text-blue-900">Services</Link>
                <span className="mx-2">/</span>
                <span className="text-blue-900">{service.title}</span>
              </nav>
              <h1 className="text-2xl md:text-3xl font-bold text-blue-900">
                {service.title}
              </h1>
            </div>
            <Link
              to="/services"
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Back to Services"
            >
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </Link>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <div className="relative">
        <div className="h-64 md:h-96 overflow-hidden">
          <img 
            src={service.heroImage} 
            alt={`${service.title} in Chicago`}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-blue-900/80 to-transparent"></div>
        </div>
        <div className="absolute inset-0 flex items-center">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            <div className="max-w-2xl text-white">
              <h2 className="text-3xl md:text-5xl font-bold mb-4">{service.title}</h2>
              <p className="text-lg md:text-xl text-gray-200">{service.description}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="bg-blue-900 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-3 gap-8 text-center text-white">
            {service.stats.map((stat, index) => (
              <div key={index}>
                <div className="text-3xl md:text-4xl font-bold text-red-400">{stat.value}</div>
                <div className="text-sm md:text-base text-gray-300">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid lg:grid-cols-3 gap-12">
          {/* Main Content */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
              <h2 className="text-2xl font-bold text-blue-900 mb-6">
                {service.title} in Chicago
              </h2>
              <div className="prose max-w-none text-gray-700">
                {service.longDescription.split('\n\n').map((paragraph, index) => (
                  <p key={index} className="mb-4">{paragraph}</p>
                ))}
              </div>
            </div>

            {/* Services List */}
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <h2 className="text-2xl font-bold text-blue-900 mb-6">
                Our {service.title} Services
              </h2>
              <div className="space-y-6">
                {service.services.map((item, index) => (
                  <div key={index} className="flex gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-red-600 rounded-lg flex items-center justify-center">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-blue-900 mb-1">{item.name}</h3>
                      <p className="text-gray-600">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            {/* CTA Card */}
            <div className="bg-gradient-to-br from-blue-900 to-red-900 rounded-2xl shadow-lg p-8 text-white mb-8 sticky top-32">
              <h3 className="text-2xl font-bold mb-4">Get a Free Estimate</h3>
              <p className="text-gray-200 mb-6">
                Ready to start your {service.title.toLowerCase()} project? Contact us today for a 
                free consultation and estimate.
              </p>
              <a 
                href="tel:+13126756000"
                className="block w-full text-center bg-white text-blue-900 hover:bg-gray-100 font-semibold py-3 px-6 rounded-xl transition-all mb-4"
                onClick={() => trackPhoneClick('312-675-6000', `service_${serviceId}`)}
              >
                <span className="flex items-center justify-center gap-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path>
                  </svg>
                  (312) 675-6000
                </span>
              </a>
              <a 
                href="mailto:hello@freedomcg.net"
                className="block w-full text-center bg-transparent border-2 border-white text-white hover:bg-white hover:text-blue-900 font-semibold py-3 px-6 rounded-xl transition-all"
              >
                Request a Quote
              </a>
            </div>

            {/* Project Types */}
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <h3 className="text-xl font-bold text-blue-900 mb-4">Project Types</h3>
              <ul className="space-y-3">
                {service.projectTypes.map((type, index) => (
                  <li key={index} className="flex items-center gap-3 text-gray-700">
                    <svg className="w-5 h-5 text-red-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
                    </svg>
                    {type}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Related Services */}
      <div className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-blue-900 mb-8 text-center">
            Other Services
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {Object.entries(serviceData)
              .filter(([key]) => key !== serviceId)
              .slice(0, 3)
              .map(([key, data]) => (
                <Link
                  key={key}
                  to={`/services/${key}`}
                  className="bg-slate-50 hover:bg-blue-50 rounded-xl p-6 transition-colors group"
                >
                  <h3 className="text-lg font-bold text-blue-900 group-hover:text-red-600 mb-2 transition-colors">
                    {data.title}
                  </h3>
                  <p className="text-gray-600 text-sm line-clamp-2">
                    {data.description}
                  </p>
                </Link>
              ))}
          </div>
          <div className="text-center mt-8">
            <Link
              to="/services"
              className="inline-flex items-center gap-2 text-blue-900 hover:text-red-600 font-semibold transition-colors"
            >
              View All Services
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
              </svg>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ServiceDetail;
